import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FinanceTabs, FinanceHeader, money, isScopedFinanceUser } from "./financeShell";

const COUNT_WIDGETS = [
  ["draft_invoices", "Draft Invoices", "FilePen", "bg-slate-100 text-slate-600", { status: "draft" }],
  ["open_invoices", "Open Invoices", "FileText", "bg-blue-100 text-blue-700", { status: "issued" }],
  ["awaiting_payment", "Awaiting Payment", "Clock", "bg-amber-100 text-amber-700", { status: "sent" }],
  ["overdue", "Overdue", "AlarmClock", "bg-rose-100 text-rose-700", { status: "overdue" }],
  ["paid", "Paid", "CheckCircle2", "bg-emerald-100 text-emerald-700", { status: "paid" }],
  ["payment_plans", "Payment Plans", "CalendarClock", "bg-violet-100 text-violet-700", {}],
];
const MONEY_WIDGETS = [
  ["accounts_receivable", "Accounts Receivable", "Landmark", "text-rose-600"],
  ["revenue_this_month", "Revenue (MTD)", "TrendingUp", "text-emerald-600"],
  ["payments_today", "Payments Today", "Banknote", "text-blue-600"],
  ["credits_issued", "Credits Issued", "PlusCircle", "text-cyan-600"],
  ["refunds", "Refunds", "RotateCcw", "text-amber-600"],
];

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [pipe, setPipe] = useState(null);

  // Gate C: scoped Finance users cannot access the firm-wide dashboard —
  // redirect them to the scoped invoice list.
  useEffect(() => {
    if (isScopedFinanceUser(user)) {
      navigate("/finance/invoices", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/finance/dashboard").then((r) => setW(r.data.widgets)).catch(() => setW({}));
    api.get("/finance/pipeline").then((r) => setPipe(r.data.pipeline)).catch(() => setPipe({}));
  }, []);

  const go = (params) => navigate(`/finance/invoices?${new URLSearchParams(params)}`);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="finance-dashboard">
      <FinanceHeader title="Finance Dashboard" subtitle="Quote-to-Cash overview — invoicing, collections and revenue.">
        <button onClick={() => navigate("/finance/invoices")} data-testid="fin-dash-new-invoice"
          className="inline-flex items-center gap-2 bg-[#171B2E] hover:bg-[#222842] text-white text-sm rounded-lg px-4 py-2.5">
          <Icons.Plus className="h-4 w-4 text-[#F5A623]" />New / Generate Invoice
        </button>
      </FinanceHeader>
      <FinanceTabs user={user} />

      {!w ? <div className="py-20 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623] inline" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="fin-count-widgets">
            {COUNT_WIDGETS.map(([key, label, icon, tone, params]) => {
              const Icon = Icons[icon];
              return (
                <button key={key} onClick={() => go(params)} data-testid={`fin-widget-${key}`}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 text-left hover:border-slate-300 hover:shadow transition-all">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tone} mb-2.5`}><Icon className="h-4.5 w-4.5" /></div>
                  <div className="text-2xl font-bold text-slate-900">{w[key] ?? 0}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="fin-money-widgets">
            {MONEY_WIDGETS.map(([key, label, icon, tone]) => {
              const Icon = Icons[icon];
              return (
                <div key={key} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid={`fin-money-${key}`}>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500"><Icon className={`h-4 w-4 ${tone}`} />{label}</div>
                  <div className="text-xl font-bold text-slate-900 mt-1.5">{money(w[key])}</div>
                </div>
              );
            })}
          </div>

          {pipe && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="fin-pipeline-summary">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-800">Revenue Pipeline</h2>
                <button onClick={() => navigate("/finance/pipeline")} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">View detail <Icons.ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[["estimated", "Estimated"], ["proposed", "Proposed"], ["contracted", "Contracted"], ["invoiced", "Invoiced"], ["collected", "Collected"], ["recognized", "Recognized"]].map(([k, l]) => (
                  <div key={k} className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                    <div className="text-[11px] text-slate-400 uppercase tracking-wide">{l}</div>
                    <div className="text-base font-bold text-slate-800 mt-1">{money(pipe[k])}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
